// pipe (floor stations, Task 0): the station surface as DATA — the ONE client-side list of the five
// floor-station screens, and the ONE way to read the server's per-station verdict.
//
// It is deliberately a thin mirror of the SERVER's `rbac::station_key` tokens: the authority lives
// entirely in `/me/provisioning`'s `stations` map (`rbac::may_work_station`, composed from the very
// gates the matching write path runs). This file holds only what the client alone knows — which
// route a station lives at and which i18n key names it — so nothing here can widen access.
//
// PURE by construction: no React, no DI, no runtime ErpService import. Nav rows, the station shells
// and their specs all read it, and it resolves cleanly under happy-dom.
//
// The full contract the five station slices build against — the shared components, the
// allowed_transitions rule, the facility pin, the picker props and the server endpoints — is
// `docs/floor-stations-contract.md`.
import type { ProvisioningInfo, StationKey } from '../../../../modules/erp';

export type { StationKey };

/** The i18n keys naming the stations in navigation. Declared as a literal union (not a bare
 *  `string`) so `t[station.labelKey]()` type-checks against the generated i18n interface — a
 *  mistyped key is a compile error, never a nav row rendering its own key at a floor
 *  station. */
export type StationLabelKey =
  | 'com.affine.pipe.stations.wipe.nav'
  | 'com.affine.pipe.stations.qc.nav'
  | 'com.affine.pipe.stations.dispatch.nav'
  | 'com.affine.pipe.stations.outbound.nav'
  | 'com.affine.pipe.stations.pallets.nav'
  | 'com.affine.pipe.stations.sort.nav'
  | 'com.affine.pipe.stations.invoices.nav';

export interface StationDescriptor {
  /** The wire token — field-for-field with the Rust `rbac::station_key`. */
  key: StationKey;
  /** The workbench route the station screen is registered at. */
  path: string;
  /** The i18n key naming the station in navigation (resolve with `t[...]()` at the call site). */
  labelKey: StationLabelKey;
  /** Which sidebar family the entry belongs to: the Operations sub-nav, or its own sidebar entry. */
  family: 'ops' | 'finance';
}

/** Every station, in floor order (SSOT — iterate this, never a hand-copied list, so a new station
 *  cannot silently skip a nav row or its spec). Mirrors the Rust `ALL_STATIONS`. */
export const STATIONS: readonly StationDescriptor[] = [
  {
    key: 'wipe',
    path: '/ops/wipe',
    labelKey: 'com.affine.pipe.stations.wipe.nav',
    family: 'ops',
  },
  {
    key: 'qc',
    path: '/ops/qc',
    labelKey: 'com.affine.pipe.stations.qc.nav',
    family: 'ops',
  },
  {
    key: 'dispatch',
    path: '/ops/dispatch',
    labelKey: 'com.affine.pipe.stations.dispatch.nav',
    family: 'ops',
  },
  {
    key: 'outbound',
    path: '/ops/outbound',
    labelKey: 'com.affine.pipe.stations.outbound.nav',
    family: 'ops',
  },
  {
    key: 'pallets',
    path: '/ops/pallets',
    labelKey: 'com.affine.pipe.stations.pallets.nav',
    family: 'ops',
  },
  {
    key: 'sort',
    path: '/ops/sort',
    labelKey: 'com.affine.pipe.stations.sort.nav',
    family: 'ops',
  },
  {
    key: 'invoices',
    path: '/finance/invoices',
    labelKey: 'com.affine.pipe.stations.invoices.nav',
    family: 'finance',
  },
] as const;

/**
 * The ONE read of the server's station verdict. FAIL-CLOSED at every step: unresolved provisioning
 * (`null`, still loading, or a failed fetch), a not-yet-redeployed server that omits `stations`
 * entirely, or a station absent from the map ALL resolve to `false`, so a station affordance can
 * never flash for an actor the server would deny. Strictly `=== true`, never a truthiness test.
 *
 * This is an AFFORDANCE read, never an authorization: the Core API re-checks every request, and
 * per-row actionability still comes from that row's own server-computed `allowed_transitions`.
 */
export function stationGranted(
  provisioning: Pick<ProvisioningInfo, 'stations'> | null | undefined,
  key: StationKey
): boolean {
  return provisioning?.stations?.[key] === true;
}

/** The stations of one sidebar family that the server actually granted, in floor order. */
export function grantedStations(
  provisioning: Pick<ProvisioningInfo, 'stations'> | null | undefined,
  family: StationDescriptor['family']
): StationDescriptor[] {
  return STATIONS.filter(
    s => s.family === family && stationGranted(provisioning, s.key)
  );
}
